<script lang="ts">
  import { uniqueId } from 'lodash-es'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { loadInternalLink } from '#app/lib/utils'
  import Dropdown from '#components/dropdown.svelte'
  import { updateItems } from '#inventory/lib/item_actions'
  import { getItemTransactionData, type SerializedItem } from '#inventory/lib/items'
  import { transactionsData, type TransactionData } from '#inventory/lib/transactions_data'
  import { I18n, i18n } from '#user/lib/i18n'

  let {
    item = $bindable(),
    flash = $bindable(),
    large = false,
    dropdownWidthReferenceEl = null,
  }: {
    item: SerializedItem
    flash?: FlashState
    large?: boolean
    dropdownWidthReferenceEl?: HTMLElement
  } = $props()

  const { pathname, restricted } = item

  const currentTransaction: TransactionData = $derived(getItemTransactionData(item.transaction))

  const menuId = uniqueId('item-transaction-box-')

  let savedTransactionName = item.transaction
  async function updateTransaction (newTransactionData) {
    try {
      if (savedTransactionName === newTransactionData.id) return
      item.transaction = newTransactionData.id
      await updateItems({
        items: [ item ],
        attribute: 'transaction',
        value: newTransactionData.id,
      })
      savedTransactionName = item.transaction
    } catch (err) {
      // Restore saved value
      item.transaction = savedTransactionName
      flash = err
    }
  }
</script>

<div class="item-card-box" class:large>
  {#if restricted}
    {#if currentTransaction}
      <a href={pathname} onclick={loadInternalLink}>
        {@html icon(currentTransaction.icon)}
        {i18n(currentTransaction.labelShort)}
      </a>
    {/if}
  {:else}
    <Dropdown
      buttonTitle={i18n('Select transaction mode')}
      clickOnContentShouldCloseDropdown={true}
      align="left"
      {dropdownWidthReferenceEl}
      alignDropdownWidthOnButton={large}
    >
      {#snippet buttonInner()}
        <div class="button-inner {currentTransaction.id}">
          <div class="icon">
            {@html icon(currentTransaction.icon)}
            {#if !large}{@html icon('caret-down')}{/if}
          </div>
          {#if large}
            <div class="rest">
              <span>{I18n(currentTransaction.labelShort)}</span>
              {@html icon('caret-down')}
            </div>
          {/if}
        </div>
      {/snippet}
      {#snippet dropdownContent()}
        <div class="dropdown-content">
          <label for={menuId}>{I18n('available for')}:</label>
          <div id={menuId} role="menu">
            {#each Object.values(transactionsData) as transaction (transaction.id)}
              <div>
                <button
                  role="menuitem"
                  class:selected={transaction.id === currentTransaction.id}
                  onclick={() => updateTransaction(transaction)}
                  title={I18n(transaction.labelShort)}
                >
                  {@html icon(transaction.icon)} {i18n(transaction.label)}
                </button>
              </div>
            {/each}
          </div>
        </div>
      {/snippet}
    </Dropdown>
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  @import "inventory/scss/item_card_box";
  .button-inner{
    .icon{
      color: white;
    }
    &.giving{
      .icon{
        background-color: $giving-color;
      }
    }
    &.lending{
      .icon{
        background-color: $lending-color;
      }
    }
    &.selling{
      .icon{
        background-color: $selling-color;
      }
    }
    &.inventorying{
      .icon{
        background-color: $inventorying-color;
      }
    }
  }
  label{
    text-align: start;
    font-size: 1rem;
    padding: 0.2em 0.5em;
  }
  [role="menuitem"]{
    text-align: start;
    width: 100%;
    padding: 0.5em;
    &.selected{
      background-color: #666;
      &, :global(.fa){
        color: white;
      }
    }
    &:not(.selected){
      @include bg-hover(white);
    }
  }
</style>
